/**
 * What: Fire-and-forget identifier-only client failure reports.
 * Transport / data-flow: Authenticated POSTs to the Worker's `/api/client-diagnostics`
 * route, which logs them and forwards to PostHog Error Tracking. Values never leave
 * the client — schema/paths are fixed identifier strings, never error text or payloads.
 *
 */
import { resolveMerchantErrorMessage } from './merchantErrors'
import { shopifyApiFetch, type ShopifyDiagnosticScope } from './shopifyApiFetch'
import { parseUiEvent, type UiEventInput } from '@testfeed/observability/ui-events'
import { shopifyUiPages } from './shopifyTrackingCatalog'

// Upper bounds mirror the /api/client-diagnostics zod contract: over-budget payloads
// are rejected with 400, so clamp here rather than losing the report entirely.
const MAX_DIAGNOSTIC_STRING = 120
const MAX_DIAGNOSTIC_PATHS = 20
const MAX_DIAGNOSTIC_ROUTE = 256
const MAX_DIAGNOSTIC_CORRELATION_ID = 256

function logReportingFailure(schema: string, status?: number) {
  // Token/network errors can contain signed URLs. Report fixed identifiers only;
  // never pass the original exception or a rejected response body to the console.
  console.error('Failed to report client diagnostic', {
    schema: schema.slice(0, MAX_DIAGNOSTIC_STRING),
    code: 'CLIENT_DIAGNOSTIC_TRANSPORT_FAILED',
    ...(status !== undefined ? { status } : {}),
  })
}

export type ClientDiagnosticInput = {
  /** Omitted means 'error'. 'event' records a normal analytics event named by `schema`. */
  kind?: 'error' | 'event'
  schema: string
  paths: string[]
  /** Fixed operation identity, such as `source:initial-state` or `action:evaluate-change`. */
  operation?: string
  setupId?: string
  draftId?: string
  status?: number
  code?: string
  /** Correlation ID returned by the failed API response, not the diagnostics intake request. */
  correlationId?: string
  /** First rejected contract path, when a checked response could not be parsed. */
  validationPath?: string
}

/** Strict product operation, separate from errors and the finite legacy-event migration path. */
export function reportClientUiEvent(
  shopify: Parameters<typeof shopifyApiFetch>[0],
  event: UiEventInput,
  diagnosticScope: ShopifyDiagnosticScope,
): void {
  try {
    const checked = parseUiEvent(event, shopifyUiPages)
    void shopifyApiFetch(shopify, '/api/client-diagnostics', {
      diagnosticScope,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'ui-event', event: checked }),
        keepalive: true,
      },
    })
      .then((response) => {
        if (!response.ok) logReportingFailure('ui-event', response.status)
      })
      .catch(() => logReportingFailure('ui-event'))
  } catch {
    // Fixed, nonrecursive diagnostics: analytics can never undo a fulfilled business operation.
    console.error('Failed to report client diagnostic', {
      schema: 'ui-event',
      code: 'CLIENT_DIAGNOSTIC_TRANSPORT_FAILED',
    })
  }
}

/** Reports a client-side failure the Worker may never see (scope guards, network, query errors). */
export function reportClientDiagnostic(
  shopify: Parameters<typeof shopifyApiFetch>[0],
  input: ClientDiagnosticInput,
  options?: { signal: AbortSignal; onDelivery?: (outcome: 'intake_acknowledged' | 'transport_failed') => void },
): void {
  const body = JSON.stringify({
    ...(input.kind ? { kind: input.kind } : {}),
    schema: input.schema.slice(0, MAX_DIAGNOSTIC_STRING),
    paths: input.paths.slice(0, MAX_DIAGNOSTIC_PATHS).map((path) => path.slice(0, MAX_DIAGNOSTIC_STRING)),
    ...(input.operation ? { operation: input.operation.slice(0, MAX_DIAGNOSTIC_STRING) } : {}),
    route:
      typeof globalThis.window !== 'undefined'
        ? globalThis.window.location.pathname.slice(0, MAX_DIAGNOSTIC_ROUTE)
        : 'unknown',
    ...(input.setupId ? { setupId: input.setupId.slice(0, MAX_DIAGNOSTIC_STRING) } : {}),
    ...(input.draftId ? { draftId: input.draftId.slice(0, 256) } : {}),
    ...(typeof input.status === 'number' && Number.isFinite(input.status) ? { status: Math.trunc(input.status) } : {}),
    ...(input.code ? { code: input.code.slice(0, MAX_DIAGNOSTIC_STRING) } : {}),
    ...(input.correlationId ? { correlationId: input.correlationId.slice(0, MAX_DIAGNOSTIC_CORRELATION_ID) } : {}),
    ...(input.validationPath ? { validationPath: input.validationPath.slice(0, MAX_DIAGNOSTIC_STRING) } : {}),
  })
  try {
    const report = shopifyApiFetch(shopify, '/api/client-diagnostics', {
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        signal: options?.signal,
      },
    })
    report
      .then((response) => {
        if (!response.ok) logReportingFailure(input.schema, response.status)
        options?.onDelivery?.(response.ok ? 'intake_acknowledged' : 'transport_failed')
      })
      .catch(() => {
        logReportingFailure(input.schema)
        options?.onDelivery?.('transport_failed')
      })
  } catch {
    options?.onDelivery?.('transport_failed')
    console.error('Failed to report client diagnostic', {
      schema: input.schema.slice(0, MAX_DIAGNOSTIC_STRING),
      code: 'CLIENT_DIAGNOSTIC_TRANSPORT_FAILED',
    })
  }
}

/**
 * Resolves merchant-safe wording for a failed action AND reports the failure.
 * Fusing the two keeps them inseparable: every shown failure is traced, and no
 * report fires without a shown failure. Use only where the returned message is
 * displayed for a failed merchant action — never for silent fallbacks.
 */
export function resolveAndReportMerchantError(
  shopify: Parameters<typeof shopifyApiFetch>[0],
  error: unknown,
  fallback: string,
  input: ClientDiagnosticInput,
): string {
  // Status and code come from the error itself unless the caller names them.
  const record: {
    status?: unknown
    code?: unknown
    correlationId?: unknown
  } = typeof error === 'object' && error !== null ? error : {}
  const fromError = {
    ...(typeof record.status === 'number' ? { status: record.status } : {}),
    ...(typeof record.code === 'string' ? { code: record.code } : {}),
    ...(typeof record.correlationId === 'string' ? { correlationId: record.correlationId } : {}),
  }
  reportClientDiagnostic(shopify, { ...fromError, ...input })
  return resolveMerchantErrorMessage(error, fallback)
}
